import { Directive, ElementRef, Input, OnInit } from '@angular/core';
import { AUTO_STYLE, keyframes, style } from '@angular/animations';

import { NgStaticAnimationService } from '../../shared/services/static-animation.service';
import { EnumNgEventName } from '../../shared/event-animation.data';
import { INgAnimationStates, INgAnimationTiming, INgEffectOptions } from '../../shared/event-animation.interface';


/**------------------------------------------------------
 * Animation State Definitions
 */
function growRotate(scale: number | undefined, rotate: number | undefined): INgAnimationStates {

	//0 - set the default if no specific value is given
	scale  = scale  ? scale  : 1.1;
	rotate = rotate ? rotate : 4;

	//1 - create the animations state
	const animationStates: INgAnimationStates = {
		start 	: keyframes([
			style({
				visibility 				: AUTO_STYLE,
				transform  				: `scale3d(${scale}, ${scale}, ${scale}) rotate(${rotate}deg)`,
				'transition-property' 	: 'transform',
				easing 					: 'ease',
				offset 					: 0
			}),
			style({
				transform 				: 'scale3d(1, 1, 1) rotate(0)',
				'transition-property' 	: 'transform',
				easing 					: 'ease',
				offset 					: 1
			})
		]),
		end		: keyframes([
			style({
				visibility 				: AUTO_STYLE,
				transform  				: 'scale3d(1, 1, 1) rotate(0)',
				'transition-property' 	: 'transform',
				easing 					: 'ease',
				offset 					: 0
			}),
			style({
				transform 				: `scale3d(${scale}, ${scale}, ${scale}) rotate(${rotate}deg)`,
				'transition-property' 	: 'transform',
				easing 					: 'ease',
				offset 					: 1
			})
		])
	};
	return animationStates;
}

/**------------------------------------------------------
 * Grow and Rotate on Event
 */
@Directive({
    selector: '[ngGrowRotateOnEvent]',
    providers: [NgStaticAnimationService],
    standalone: false
})
export class NgGrowRotateOnEventDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomEffectOptions> = null!;
	@Input() targetElement: HTMLElement = null!;

	constructor(
		private elementRef				: ElementRef,
		private staticAnimationService 	: NgStaticAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the scale and rotate values are relative to the animation functionality
		if (this.effectOptions?.scale  && this.effectOptions?.scale  < 1) throw new Error(`GrowRotateOnEventDirective => ngOnInit => FATAL ERROR: Provided scale value should be positive number: ${this.effectOptions?.scale}`);
		if (this.effectOptions?.rotate && this.effectOptions?.rotate < 1) throw new Error(`GrowRotateOnEventDirective => ngOnInit => FATAL ERROR: Provided rotate value should be positive number: ${this.effectOptions?.rotate}`);

		//1 - setup the Animation
		this.staticAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: growRotate(this.effectOptions?.scale, this.effectOptions?.rotate),
			event			: this.effectOptions?.event!,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay
			}
		});
	}
}


/**------------------------------------------------------
 * Grow and Rotate on Mouse Hover
 */
@Directive({
    selector: '[ngGrowRotateOnMouseHover]',
    providers: [NgStaticAnimationService],
    standalone: false
})
export class NgGrowRotateOnMouseHoverDirective implements OnInit {

	//** Input Decorators */
	@Input() timingOptions: Partial<INgCustomAnimationTiming> = null!;
	@Input() targetElement: HTMLElement						= null!;

	constructor(
		private elementRef				: ElementRef,
		private staticAnimationService 	: NgStaticAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the scale and rotate values are relative to the animation functionality
		if (this.timingOptions?.scale  && this.timingOptions?.scale  < 1) throw new Error(`GrowRotateOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided scale value should be positive number: ${this.timingOptions?.scale}`);
		if (this.timingOptions?.rotate && this.timingOptions?.rotate < 1) throw new Error(`GrowRotateOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided rotate value should be positive number: ${this.timingOptions?.rotate}`);

		//1 - setup the Animation
		this.staticAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: growRotate(this.timingOptions?.scale, this.timingOptions?.rotate),
			event			: EnumNgEventName.MouseHover,
			timing			: this.timingOptions
		});
	}
}


//** Interfaces --------------------------------- */
interface INgCustomEffectOptions extends INgEffectOptions {
	scale  : number;
	rotate : number;
}

interface INgCustomAnimationTiming extends INgAnimationTiming {
	scale  : number;
	rotate : number;
}
